import React, { useState, useEffect } from "react";
import { Container } from "@mui/material";
import { useForm } from "@mantine/form";
import { Endpoints } from "../../Lib/endpoints";
import { PerformRequest } from "../../Lib/usePerformRequest";
import { CourseProps } from "../../Lib/types";
import { NonPaginatedResponse } from "../../Lib/responses";
import { DefaultWarningNotification } from "../../Lib/methods";
import { useTranslation } from "react-i18next";
import {
  TextInput,
  Button,
  Card,
  Group,
  Text,
  Title,
  Stack,
  Badge,
  Image,
  Container as MantineContainer,
  Paper,
  Loader,
  Alert,
  ActionIcon,
  Grid,
  SimpleGrid,
} from "@mantine/core";
import {
  IconSearch,
  IconBook,
  IconUsers,
  IconStar,
  IconClock,
  IconPlayerPlay,
  IconArrowRight,
  IconX,
} from "@tabler/icons-react";
import { motion, AnimatePresence } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { Colors } from "../../Lib/data";
import { getEllipsisFromString } from "../../Lib/methods";
import TopNav from "../../Components/TopNav";

import "./styles.scss";

const SearchPage: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [hasSearched, setHasSearched] = useState(false);

  const searchForm = useForm({
    initialValues: {
      search: "",
    },
    validate: {
      search: (value) => (value && value.length > 0 ? null : t("error.required")),
    },
  });
  const [isLoading, setLoading] = useState(false);
  const [searchResults, setSearchResults] = useState<CourseProps[]>([]);

  const SearchForCourses = async (search: string) => {
    if (!search.trim()) return;

    setLoading(true);
    setHasSearched(true);

    const r = await PerformRequest<NonPaginatedResponse<CourseProps[]>>({
      method: Endpoints.GetSearchCourses().method,
      route: Endpoints.GetSearchCourses().route,
      data: { search },
      callback: () => {
        setLoading(false);
      },
    });
    setLoading(false);

    if (r) {
      if (r?.data?.data?.length > 0) {
        setSearchResults(r.data.data);
      } else {
        setSearchResults([]);
        DefaultWarningNotification(t("courses.noCourses"));
      }
    }
  };

  const handleSearch = (values: { search: string }) => {
    SearchForCourses(values.search);
  };

  const handleCourseClick = (courseId: string) => {
    navigate(`/d/course/${courseId}`);
  };

  const clearSearch = () => {
    searchForm.reset();
    setSearchResults([]);
    setHasSearched(false);
  };

  return (
    <div className="search-page-container">
      <TopNav />

      <MantineContainer size="lg" py="xl">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          {/* Hero Section */}
          <div className="search-hero">
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.2, duration: 0.5 }}
              className="search-hero-content"
            >
              <Title order={1} className="search-title">
                {t("common.search")}
              </Title>
              <Text size="lg" c="dimmed" className="search-subtitle">
                Discover amazing courses and expand your knowledge
              </Text>
            </motion.div>

            {/* Search Form */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4, duration: 0.5 }}
              className="search-form-container"
            >
              <Paper shadow="lg" radius="xl" p="xl" className="search-form-paper">
                <form onSubmit={searchForm.onSubmit(handleSearch)}>
                  <Group gap="md" align="flex-end">
                    <TextInput
                      placeholder="Search for courses or topics..."
                      size="lg"
                      radius="lg"
                      leftSection={<IconSearch size={20} />}
                      rightSection={
                        searchForm.values.search && (
                          <ActionIcon variant="subtle" size="sm" onClick={clearSearch} color="gray">
                            <IconX size={16} />
                          </ActionIcon>
                        )
                      }
                      {...searchForm.getInputProps("search")}
                      className="search-input"
                      autoFocus
                    />
                    <Button
                      type="submit"
                      size="lg"
                      radius="lg"
                      loading={isLoading}
                      disabled={isLoading}
                      leftSection={<IconSearch size={18} />}
                      className="search-button"
                    >
                      {isLoading ? "Searching..." : "Search"}
                    </Button>
                  </Group>
                </form>
              </Paper>
            </motion.div>
          </div>

          {/* Search Results */}
          <AnimatePresence mode="wait">
            {hasSearched && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                transition={{ duration: 0.4 }}
                className="search-results-section"
              >
                {isLoading ? (
                  <div className="search-loading">
                    <Loader size="lg" color={Colors.PurplePrimary} />
                    <Text size="lg" c="dimmed" mt="md">
                      Searching for courses...
                    </Text>
                  </div>
                ) : searchResults.length > 0 ? (
                  <div className="search-results">
                    <Group justify="space-between" align="center" mb="xl">
                      <Title order={2} className="results-title">
                        Found {searchResults.length} course
                        {searchResults.length !== 1 ? "s" : ""}
                      </Title>
                      <Badge
                        size="lg"
                        variant="light"
                        color={Colors.PurplePrimary}
                        leftSection={<IconBook size={16} />}
                      >
                        {searchResults.length} Results
                      </Badge>
                    </Group>

                    <SimpleGrid
                      cols={{ base: 1, md: 2, lg: 3 }}
                      spacing="lg"
                      className="results-grid"
                    >
                      <AnimatePresence>
                        {searchResults.map((course, index) => (
                          <motion.div
                            key={course.id}
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: -20 }}
                            transition={{ delay: index * 0.1, duration: 0.4 }}
                          >
                            <Card
                              className="course-card"
                              shadow="md"
                              radius="lg"
                              withBorder
                              onClick={() => handleCourseClick(course.id)}
                            >
                              <div className="course-image-container">
                                <Image
                                  src={
                                    course.thumbnail ||
                                    course.images[0] ||
                                    "https://placehold.co/600x400"
                                  }
                                  alt={course.name}
                                  className="course-image"
                                />
                                <div className="course-overlay">
                                  <ActionIcon
                                    size="lg"
                                    radius="xl"
                                    color="white"
                                    className="play-button"
                                  >
                                    <IconPlayerPlay size={20} />
                                  </ActionIcon>
                                </div>
                              </div>

                              <Stack gap="md" p="lg">
                                <div>
                                  <Title order={3} className="course-title">
                                    {course.name}
                                  </Title>
                                  <Text
                                    size="sm"
                                    c="dimmed"
                                    lineClamp={2}
                                    className="course-description"
                                  >
                                    {getEllipsisFromString(course.description, 120)}
                                  </Text>
                                </div>

                                <Group gap="sm" className="course-badges">
                                  <Badge
                                    leftSection={<IconClock size={12} />}
                                    color="blue"
                                    variant="light"
                                    size="sm"
                                  >
                                    8 weeks
                                  </Badge>
                                  <Badge
                                    leftSection={<IconUsers size={12} />}
                                    color="green"
                                    variant="light"
                                    size="sm"
                                  >
                                    1.2k students
                                  </Badge>
                                  <Badge
                                    leftSection={<IconStar size={12} />}
                                    color="yellow"
                                    variant="light"
                                    size="sm"
                                  >
                                    4.8 rating
                                  </Badge>
                                </Group>

                                <Button
                                  fullWidth
                                  variant="light"
                                  color={Colors.PurplePrimary}
                                  rightSection={<IconArrowRight size={16} />}
                                  className="view-course-button"
                                >
                                  View Course
                                </Button>
                              </Stack>
                            </Card>
                          </motion.div>
                        ))}
                      </AnimatePresence>
                    </SimpleGrid>
                  </div>
                ) : (
                  <div className="no-results">
                    <motion.div
                      initial={{ opacity: 0, scale: 0.8 }}
                      animate={{ opacity: 1, scale: 1 }}
                      transition={{ delay: 0.2, duration: 0.5 }}
                      className="no-results-content"
                    >
                      <IconSearch size={64} className="no-results-icon" />
                      <Title order={2} className="no-results-title">
                        No courses found
                      </Title>
                      <Text size="lg" c="dimmed" className="no-results-text">
                        Try adjusting your search terms or browse our course catalog
                      </Text>
                      <Button
                        size="lg"
                        variant="light"
                        color={Colors.PurplePrimary}
                        onClick={() => navigate("/d/courses")}
                        leftSection={<IconBook size={18} />}
                        className="browse-courses-button"
                      >
                        Browse All Courses
                      </Button>
                    </motion.div>
                  </div>
                )}
              </motion.div>
            )}
          </AnimatePresence>
        </motion.div>
      </MantineContainer>
    </div>
  );
};

export default SearchPage;
